import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { includedGear, gearColumns, media, GearItem, GearStatus } from '../data/product';
import { requestOffer } from '../utils/offerBus';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { useQuickBuild, quickAddPrices } from '../hooks/useQuickBuild';
import { QuickBuildPanel } from './QuickBuildPanel';
import { ReviewsStrip } from './BannerOverlays';
import { money } from './QuantityStepper';
import type { QuickAddId } from '../data/product';
const quickAddLabels: Record<QuickAddId, string> = {
  fullLens: 'Add the other 2 lenses',
  hardCase: 'Add hard case',
  careKit: 'Add care kit',
  rx: 'Add RX insert',
  lifetime: 'Add Lifetime protection'
};
type ColumnKey = (typeof gearColumns)[number]['key'];

/**
 * What ships with each way to buy, side by side: the $29.95 Essential only
 * includes what's listed for it; everything else is an add-on or comes with
 * the Complete System and multi-packs.
 */
function goToColumn(key: ColumnKey, event: React.MouseEvent<HTMLAnchorElement>) {
  if (key === 'essential') return; // plain #build-your-x1 anchor
  event.preventDefault();
  requestOffer(key === 'complete' ? 'single' : 'trio');
}
function StatusCell({
  status,
  column
}: {
  status: GearStatus;
  column: string;
}) {
  const Icon = status.kind === 'included' ? CheckIcon : status.kind === 'addon' ? PlusIcon : MinusIcon;
  const tone = status.kind === 'included' ? 'text-offwhite' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-500';
  const iconTone = status.kind === 'included' ? 'text-grit-500' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-600';
  return <div className="bg-ink-950 px-3 py-2.5 lg:bg-transparent lg:px-0 lg:py-0" data-id="element-644" data-mp="2d2gzfkn9630y">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-steel-500 lg:hidden" data-id="element-645" data-mp="nnivhrdyy5v4">{column}</p>
      <p className={`mt-1 flex items-start gap-1.5 text-[13px] font-semibold leading-snug lg:mt-0 ${tone}`} data-id="element-646" data-mp="ip7mtjzeu1eh">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} aria-hidden="true" data-id="element-647" data-mp="2irqpj2yryp6t" />
        {status.label}
      </p>
    </div>;
}
const PROTECTION_BADGE = 'https://cdn.magicpatterns.com/uploads/fgn15VRLFuUHNXjK51LK5X/0DA33D70-743E-4305-91DE-D0EDB3114662.png';
function GearThumb({
  item
}: {
  item: GearItem;
}) {
  if (item.image) {
    return <div className="aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-900" data-id="element-648" data-mp="31pa04b0vsfvc">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" data-id="element-649" data-mp="36nl7mf3peeae" />
      </div>;
  }
  return <div className="aspect-[4/3] w-full overflow-hidden border border-[#f2c94c]/40 bg-black" data-id="element-650" data-mp="3bfw9volx2c64">
      <img src={PROTECTION_BADGE} alt="GritVue Lifetime Protection breakage coverage badge" className="h-full w-full object-contain" loading="lazy" data-id="element-651" data-mp="3gcmpkatt75r0" />
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  const build = useQuickBuild();
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-652" data-mp="18rwbhk4cy2ue">
      <div className="grid gap-6 border-b border-white/10 px-5 py-6 sm:px-7 lg:grid-cols-[1fr_380px] lg:items-center" data-id="element-653" data-mp="14psygr21iq70">
        <div data-id="element-654" data-mp="zs9hvkhve1bw">
          <ReviewsStrip className="!mx-0 mb-2 !px-0" data-id="element-655" data-mp="zsst6id0lrgy" />
          <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-656" data-mp="1wi0epqdmw0kv">
            What&apos;s in the box? <span className="text-grit-500" data-id="element-657" data-mp="1nondm15f1snp">You choose.</span>
          </h3>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-steel-400" data-id="element-658" data-mp="1nmu66jmzb4en">
            The X1 Essential ships with the X1, one lens of your choice, a USB-C cable and the Quick Start Guide, plus
            only the add-ons you pick. The Complete System comes with everything below. Every X1 in a multi-pack is a
            Complete System.
          </p>
        </div>
        <figure className="border border-white/10 bg-ink-900" data-id="element-659" data-mp="1uxxgiw68rr1u">
          {/* Cropped on the right so the anti-fog spray (sold separately) stays out of frame. */}
          <div className="w-full overflow-hidden bg-black" style={{
          aspectRatio: '1105 / 1254'
        }} data-id="element-660" data-mp="1lzbx6ysy4800">
            <img src="https://cdn.magicpatterns.com/uploads/hPkSqjWLBFxj6o9jAzPCn2/D3D967EB-F303-4189-842F-BCB4DB15CDF8.png" alt="The GritVue X1 Complete System: X1 glasses, Clear, Amber and Smoke lenses, hard case, pouch and cleaning cloth" className="block h-auto w-[113.5%] max-w-none" loading="lazy" data-id="element-661" data-mp="1qyomdq7ahr5r" />
          </div>
          <figcaption className="border-t border-white/10 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-662" data-mp="29vptbgkem1cc">
            The Complete System, organized in its hard case
          </figcaption>
        </figure>
      </div>

      {/* Column headers (desktop) */}
      <div className="hidden grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] items-end gap-5 border-b border-white/10 px-7 py-4 lg:grid" data-id="element-663" data-mp="3brdrh0hdv86s">
        <span data-id="element-664" data-mp="37p9068eeqnnb" />
        <span data-id="element-665" data-mp="37ps6erup3nli" />
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-666" data-mp="3lhov0lx5nfd5">
            <span className="block font-display text-lg font-extrabold uppercase leading-none text-offwhite transition-colors duration-150 ease-grit group-hover:text-grit-500" data-id="element-667" data-mp="2ory0wnsrzu7v">
              {column.title}
            </span>
            <span className="mt-1 block text-[12px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-668" data-mp="2xr30zyjzjvm5">
              {column.price}
            </span>
          </a>)}
      </div>

      <ul className="divide-y divide-white/10" data-id="element-669" data-mp="d0kcocsxv67m">
        {includedGear.map(item => <li key={item.name} className="grid gap-4 px-5 py-5 sm:grid-cols-[140px_minmax(0,1fr)] sm:px-7 lg:grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] lg:items-center lg:gap-5" data-id="element-670" data-mp="l330rtwhk47t">
            <div className="relative" data-id="element-671" data-mp="u4ayoav3b31d">
              <GearThumb item={item} data-id="element-672" data-mp="u16pez2rph3u" />
              {item.quickAdd && build.isAdded(item.quickAdd) && <span className="absolute left-2 top-2 inline-flex items-center gap-1 bg-grit-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-950" data-id="element-673" data-mp="1ec7a8tfattj">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-674" data-mp="6bcbsfamjoij" />
                  Added
                </span>}
            </div>
            <div data-id="element-675" data-mp="g3qcd4pyup34">
              <p className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-676" data-mp="g49vgorcrjiq">
                {item.name}
              </p>
              <p className="mt-1 text-[14px] leading-relaxed text-steel-300" data-id="element-677" data-mp="3jsbjgniywf70">{item.note}</p>
              {item.quickAdd && (() => {
            const id = item.quickAdd;
            const added = build.isAdded(id);
            return <button type="button" aria-pressed={added} onClick={() => build.toggle(id)} className={`mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-3.5 py-2 font-display text-[15px] font-extrabold uppercase leading-none tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${added ? 'bg-grit-500 text-ink-950 hover:bg-grit-400' : 'text-grit-500 hover:bg-grit-500 hover:text-ink-950'}`} data-id="element-678" data-mp="37a8yj7eqizkf">
                    {added ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-679" data-mp="29kw4ui9sy5cc" /> : <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-680" data-mp="25kc7bdlbgzpo" />}
                    {added ? 'Added · Remove' : `${quickAddLabels[id]} · +${money(quickAddPrices[id])}`}
                  </button>;
          })()}
              {item.termsLink && <Link to="/legal/guarantee" className="mt-1.5 inline-block text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-681" data-mp="29z5mw6wo34r">
                  See warranty &amp; protection terms
                </Link>}
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 sm:col-span-2 lg:contents" data-id="element-682" data-mp="3aopmoepum3ua">
              {gearColumns.map(column => <StatusCell key={column.key} status={item[column.key]} column={column.title} data-id="element-683" data-mp="30t5tw16kzl9r" />)}
            </div>
          </li>)}
      </ul>

      {/* Column shortcuts (mobile / tablet) */}
      <div className="grid grid-cols-3 gap-px border-t border-white/10 bg-white/10 lg:hidden" data-id="element-684" data-mp="2rsr68v659u33">
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="bg-ink-900 px-3 py-3 transition-colors duration-150 ease-grit hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-685" data-mp="2vvdzn0igrn2e">
            <span className="block font-display text-[15px] font-extrabold uppercase leading-tight text-offwhite" data-id="element-686" data-mp="2h5t5bj7nv1bs">
              {column.title}
            </span>
            <span className="mt-0.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-grit-500" data-id="element-687" data-mp="2r50gbzqxajvq">
              {column.price}
            </span>
          </a>)}
      </div>

      <QuickBuildPanel build={build} data-id="element-688" data-mp="30ujzz7mgmhrn" />
    </section>;
}